/** Super-admin → Partners: resellers (with nested commercials) + direct
 * commercials, with editable commission terms. */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  CommercialForm,
  type CommercialFormValues,
} from "@/components/CommercialForm";
import { PlatformPageHeader } from "@/components/PlatformLayout";
import { Spinner } from "@/components/Spinner";
import { startImpersonation } from "@/lib/impersonation";
import {
  useCreatePlatformCommercial,
  usePlatformPartners,
  useResendPlatformCommercialInvite,
  useUpdatePartnerTerms,
} from "@/lib/platform";
import type {
  PlatformPartnerCommercial,
  PlatformReseller,
} from "@/lib/types";

type AnyPartner = PlatformReseller | PlatformPartnerCommercial;

function TermsEditor({
  kind,
  partner,
}: {
  kind: "reseller" | "commercial";
  partner: AnyPartner;
}) {
  const { t } = useTranslation();
  const update = useUpdatePartnerTerms();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    commission_model: partner.commission_model || "percentage",
    rate_base_pct: String(partner.rate_base_pct),
    rate_session_pct: String(partner.rate_session_pct),
    rate_signature_pct: String(partner.rate_signature_pct),
    fixed_base_eur: String(partner.fixed_base_eur ?? 0),
    fixed_session_eur: String(partner.fixed_session_eur ?? 0),
    fixed_signature_eur: String(partner.fixed_signature_eur ?? 0),
    commission_duration: partner.commission_duration,
    status: partner.status,
  });

  if (!open) {
    return (
      <button
        type="button"
        className="btn-ghost text-xs"
        onClick={() => setOpen(true)}
      >
        {t("platform.partners.editTerms")}
      </button>
    );
  }
  const isFixed = f.commission_model === "fixed";
  const axes = isFixed
    ? ([
        ["fixed_base_eur", "platform.partners.baseE"],
        ["fixed_session_eur", "platform.partners.sessionE"],
        ["fixed_signature_eur", "platform.partners.sigE"],
      ] as const)
    : ([
        ["rate_base_pct", "platform.partners.baseP"],
        ["rate_session_pct", "platform.partners.sessionP"],
        ["rate_signature_pct", "platform.partners.sigP"],
      ] as const);
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border border-nb-border bg-nb-bg/40 p-2">
      <label className="text-[11px] text-nb-text-3">
        {t("platform.partners.model")}
        <select
          className="input mt-0.5 text-xs"
          value={f.commission_model}
          onChange={(e) =>
            setF((s) => ({
              ...s,
              commission_model: e.target.value as "percentage" | "fixed",
            }))
          }
        >
          <option value="percentage">{t("platform.partners.modelPct")}</option>
          <option value="fixed">{t("platform.partners.modelFixed")}</option>
        </select>
      </label>
      {axes.map(([key, label]) => (
        <label key={key} className="text-[11px] text-nb-text-3">
          {t(label)}
          <input
            className="input mt-0.5 w-16 text-xs"
            type="number"
            step="0.01"
            value={f[key]}
            onChange={(e) => setF((s) => ({ ...s, [key]: e.target.value }))}
          />
        </label>
      ))}
      <label className="text-[11px] text-nb-text-3">
        {t("platform.partners.duration")}
        <select
          className="input mt-0.5 text-xs"
          value={f.commission_duration}
          onChange={(e) =>
            setF((s) => ({ ...s, commission_duration: e.target.value }))
          }
        >
          <option value="lifetime">{t("platform.partners.durationOption.lifetime")}</option>
          <option value="12_months">{t("platform.partners.durationOption.12_months")}</option>
          <option value="one_time">{t("platform.partners.durationOption.one_time")}</option>
        </select>
      </label>
      <label className="text-[11px] text-nb-text-3">
        {t("platform.common.status")}
        <select
          className="input mt-0.5 text-xs"
          value={f.status}
          onChange={(e) => setF((s) => ({ ...s, status: e.target.value }))}
        >
          <option value="active">{t("platform.partners.statusOption.active")}</option>
          <option value="suspended">{t("platform.partners.statusOption.suspended")}</option>
        </select>
      </label>
      <button
        type="button"
        className="btn-primary text-xs"
        disabled={update.isPending}
        onClick={() =>
          update.mutate(
            { kind, id: partner.id, ...f },
            { onSuccess: () => setOpen(false) },
          )
        }
      >
        {update.isPending ? t("common.saving") : t("common.save")}
      </button>
      <button
        type="button"
        className="btn-ghost text-xs"
        onClick={() => setOpen(false)}
      >
        {t("common.cancel")}
      </button>
    </div>
  );
}

function ResendCommercialInvite({ id }: { id: string }) {
  const { t } = useTranslation();
  const resend = useResendPlatformCommercialInvite();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        className="btn-ghost text-xs"
        disabled={resend.isPending}
        onClick={() => {
          setErr(false);
          setSentTo(null);
          resend.mutate(id, {
            onSuccess: (res) => setSentTo(res.email),
            onError: () => setErr(true),
          });
        }}
      >
        {t("platform.commercials.resend")}
      </button>
      {sentTo && (
        <span className="text-xs text-nb-ok" role="status">
          {t("platform.commercials.resendSent", { email: sentTo })}
        </span>
      )}
      {err && (
        <span className="text-xs text-nb-danger" role="alert">
          {t("platform.commercials.resendError")}
        </span>
      )}
    </span>
  );
}

function PartnerCard({
  kind,
  partner,
  subtitle,
  resend = false,
}: {
  kind: "reseller" | "commercial";
  partner: AnyPartner;
  subtitle: string;
  resend?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const openConsole = async () => {
    setOpening(true);
    try {
      await startImpersonation(kind, partner.id);
      navigate("/partner");
    } catch {
      setOpening(false);
    }
  };
  return (
    <div className="rounded-md border border-nb-border bg-slate-900/40 p-3 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <span className="font-semibold text-nb-text">{partner.name}</span>{" "}
          <span className="text-xs text-nb-text-3">{subtitle}</span>
          {partner.status !== "active" && (
            <span className="ml-2 text-xs text-cvp-warn">
              {t(`platform.partners.statusOption.${partner.status}`, partner.status)}
            </span>
          )}
          <button
            type="button"
            className="ml-3 rounded border border-nb-border px-2 py-0.5 text-xs text-nb-text-2 hover:bg-slate-800 disabled:opacity-50"
            disabled={opening}
            data-testid={`impersonate-${kind}-${partner.id}`}
            onClick={openConsole}
          >
            {opening ? t("impersonation.opening") : t("impersonation.openConsole")}
          </button>
        </div>
        <div className="text-xs text-nb-text-2">
          {partner.commission_model === "fixed"
            ? `€${partner.fixed_session_eur} / session · €${partner.fixed_base_eur} / base · €${partner.fixed_signature_eur} / sig`
            : `${partner.rate_session_pct}% / session · ${partner.rate_base_pct}% / base · ${partner.rate_signature_pct}% / sig`}
          {" · "}
          {t(`platform.partners.durationOption.${partner.commission_duration}`, partner.commission_duration)}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-xs text-nb-text-3">
        <span>{partner.org_count} {t("platform.partners.orgs")}</span>
        <span>{t("platform.partners.earned")} €{partner.commission_total_eur.toFixed(2)}</span>
        <span>{t("platform.partners.unpaid")} €{partner.commission_unpaid_eur.toFixed(2)}</span>
        <span>{t("platform.partners.payouts")} {partner.payouts_enabled ? "✓" : "—"}</span>
        <span className="font-mono">{partner.referral_code}</span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <TermsEditor kind={kind} partner={partner} />
        {resend && <ResendCommercialInvite id={partner.id} />}
      </div>
    </div>
  );
}

export default function PlatformPartnersPage() {
  const { t } = useTranslation();
  const partners = usePlatformPartners();

  if (partners.isLoading) return <Spinner fullscreen label={t("common.loading")} />;
  const data = partners.data;

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title={t("platform.nav.partners")}
        description={t("platform.partners.desc")}
      />

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("platform.partners.resellers")}
        </h2>
        {!data || data.resellers.length === 0 ? (
          <p className="text-sm text-nb-text-3">{t("platform.partners.noResellers")}</p>
        ) : (
          data.resellers.map((r) => (
            <div key={r.id} className="space-y-2">
              <PartnerCard kind="reseller" partner={r} subtitle={`@${r.slug}`} />
              {r.commercials.length > 0 && (
                <div className="ml-4 space-y-2 border-l border-nb-border pl-4">
                  <div className="text-xs uppercase tracking-wide text-nb-text-3">
                    {t("platform.partners.commercialsUnder", { name: r.name })}
                  </div>
                  {r.commercials.map((c) => (
                    <PartnerCard
                      key={c.id}
                      kind="commercial"
                      partner={c}
                      subtitle={c.email}
                    />
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("platform.partners.directCommercials")}
          </h2>
          <AddDirectCommercial />
        </div>
        {!data || data.direct_commercials.length === 0 ? (
          <p className="text-sm text-nb-text-3">{t("platform.partners.noDirect")}</p>
        ) : (
          data.direct_commercials.map((c) => (
            <PartnerCard
              key={c.id}
              kind="commercial"
              partner={c}
              subtitle={c.email}
              resend
            />
          ))
        )}
      </section>
    </div>
  );
}

/** Super-admin registers a NexBasira-direct commercial via the shared
 * CommercialForm. On success surfaces the referral code + whether an
 * invite email went out. */
function AddDirectCommercial() {
  const { t } = useTranslation();
  const create = useCreatePlatformCommercial();
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<
    { referral_code: string; invited: boolean } | null
  >(null);

  const submit = (v: CommercialFormValues) => {
    const isFixed = v.commission_model === "fixed";
    const termKeys = isFixed
      ? (["fixed_base_eur", "fixed_session_eur", "fixed_signature_eur"] as const)
      : (["rate_base_pct", "rate_session_pct", "rate_signature_pct"] as const);
    const terms: Record<string, string> = {};
    for (const k of termKeys) if (v[k] !== "") terms[k] = v[k];
    create.mutate(
      {
        name: v.name,
        email: v.email,
        phone: v.phone || undefined,
        is_company: v.is_company,
        commission_model: v.commission_model,
        commission_duration: v.commission_duration,
        ...terms,
        ...(v.is_company
          ? {
              legal_name: v.legal_name,
              registration_number: v.registration_number,
              vat_id: v.vat_id,
              address_line: v.address_line,
              city: v.city,
              postal_code: v.postal_code,
              address_country: v.address_country,
            }
          : {}),
      },
      {
        onSuccess: (res) => {
          setResult({ referral_code: res.referral_code, invited: res.invited });
          setOpen(false);
        },
      },
    );
  };

  if (!open) {
    return (
      <div className="flex flex-col items-end gap-1">
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={() => {
            setResult(null);
            setOpen(true);
          }}
        >
          + {t("platform.commercials.addDirect")}
        </button>
        {result && (
          <span className="text-xs text-nb-ok" role="status">
            {result.invited
              ? t("platform.commercials.created", { code: result.referral_code })
              : t("platform.commercials.createdNoInvite", {
                  code: result.referral_code,
                })}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="w-full space-y-2">
      <PlatformPageHeader
        title={t("platform.commercials.addTitle")}
        description={t("platform.commercials.addLead")}
      />
      <CommercialForm
        mode="create"
        submitting={create.isPending}
        error={create.isError}
        submitLabel={t("platform.commercials.addDirect")}
        onSubmit={submit}
        onCancel={() => setOpen(false)}
      />
    </div>
  );
}
